import React, {
  memo,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import cls from 'classnames';

import _isBoolean from 'lodash/isBoolean';
import _noop from 'lodash/noop';

import { useLayoutContext } from '@ott/layout-context';

import { Content, ContentChildrenClassNames } from './blocks/Content/Content';
import {
  ACCIDENTAL_TRIGGER_TIMEOUT,
  CONTENT_OFFSET,
  HOVER_LEAVE_TIMEOUT,
  PAGE_PADDING,
} from './helpers/constants';
import { Placement, Presets } from './helpers/types';

import styles from './Tooltip.scss';

export type HoverHandlers = {
  onPointerEnter: () => void;
  onPointerLeave: () => void;
} | null;

export type TooltipProps = {
  children?: ReactNode[] | ReactNode;
  /**
   * Класс для управлением стилями "плашки"
   */
  className?: string;
  /**
   * Класс для управлением стилями триггера
   */
  triggerClassName?: string;
  /**
   * Объект стилей
   */
  triggerStyle?: React.CSSProperties;
  /**
   * Передаем свои классы для управления стилями внутренних элементов:
   * 1. крестик,
   * 2. враппер контента (Данный класс может понадобиться в очень редких кейсах, например,
   * для переопределения z-index в рамках использования сторонней библиотеки (ant-design))
   */
  childrenClassNames?: ContentChildrenClassNames;
  /**
   * Используется для позиционирования относительно внешнего контролирующего узла.
   * Например, для дней в календаре, чтобы не заменять все дни на тултипы.
   * В дальнейшем может быть задеприкейчен - в большинстве случаев триггер тултипа можно стилизовать как того требуют обстоятельства.
   * При использовании - не вкладывать тултип внутрь внешнего триггера. Можно положить сиблингом.
   * */
  customTargetRef?: RefObject<HTMLElement>;
  /**
   * React element, представляющий собой контент для отображении в тултипе.
   */
  content: ReactNode;
  /**
   * Колбэк, который срабатывает непосредственно в момент клика по триггеру.
   * В качестве параметра принимает объект события event.
   * Коллбэк может пригодиться для отправки статистики или предотвращения всплытия при необходимости.
   */
  onTriggerClick?: (event: React.MouseEvent) => void;
  /**
   * Внешнее управление видимостью тултипа
   */
  isVisible?: boolean;
  /**
   * Колбэк, который вызывается при открытии/скрытии тултипа.
   * В качестве параметра передается флаг isOpen, соотвествующий состоянию тултипа.
   * Данный колбэк может быть полезен для отправки статистики.
   */
  onVisibilityChange?: (isOpen: boolean) => void;
  /**
   * Расположение контента относительно триггера.
   * Расположение применяется только в том случае, если это не вызовет уход элемента за пределы экрана.
   * В случае, если заданное расположение “вынуждает“ content уйти за пределы экрана, применяется автоматическая корректировка расположения.
   */
  placement?: Placement;
  /**
   * Передаем дата-локатор для тестирования, определяющий респонсибилити поповера/тултипа
   */
  specificLocator?: string;
  /**
   * Определяем триггер.
   * На тач девайсах не реагирует на ховер, а только на клик независимо от того, какой проп передан
   */
  triggerMode?: 'click' | 'hover';
  /**
   * На тач девайсах ховер ведет себя как клик, поэтому дефолтное поведение
   * при triggerMode = 'hover' - это срабатывание по тапу.
   * Данный проп позволяет отменить это поведение для тач девайсов.
   */
  isDisabledOnTouchDevice?: boolean;
  /**
   * Определяем нужен ли крестик, закрывающий контент
   */
  hasCloser?: boolean;
  /**
   * Цветовая схема плашки контента
   */
  preset?: Presets;
};

export type Constants = {
  baseLocator: 'tooltip' | 'popover';
};

const tooltipConstants: Constants = {
  baseLocator: 'tooltip',
};

const popoverConstants: Constants = {
  baseLocator: 'popover',
};

type CreateTooltipParams = {
  isPopoverMode?: boolean;
};

export const createTooltip = (params?: CreateTooltipParams) => {
  const { isPopoverMode } = params || {};
  const { baseLocator } = isPopoverMode ? popoverConstants : tooltipConstants;
  const defaultPreset = isPopoverMode ? undefined : 'dark';

  return ({
    children,
    childrenClassNames = {},
    className,
    content,
    customTargetRef,
    hasCloser = false,
    isDisabledOnTouchDevice = false,
    isVisible,
    onTriggerClick = _noop,
    onVisibilityChange = _noop,
    placement = 'top',
    preset = defaultPreset,
    specificLocator,
    triggerClassName,
    triggerMode = 'hover',
    triggerStyle = {},
  }: TooltipProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const isContentHovered = useRef(false);
    const isTriggerHovered = useRef(false);
    const { isTouchDevice } = useLayoutContext();
    const hasOuterControl = _isBoolean(isVisible);

    const closeTooltip = useCallback(() => {
      setIsOpen(false);
      isContentHovered.current = false;
    }, [setIsOpen]);

    const hasInternalHoverHandlers = !hasOuterControl && triggerMode === 'hover' && !isTouchDevice;

    const { triggerHoverHandlers, contentHoverHandlers } = useMemo<{
      triggerHoverHandlers: HoverHandlers;
      contentHoverHandlers: HoverHandlers;
    }>(() => {
      if (!hasInternalHoverHandlers) {
        return {
          triggerHoverHandlers: null,
          contentHoverHandlers: null,
        };
      }

      return {
        triggerHoverHandlers: {
          onPointerEnter: () => {
            isTriggerHovered.current = true;
            setTimeout(() => {
              if (isTriggerHovered.current) {
                setIsOpen(true);
              }
            }, ACCIDENTAL_TRIGGER_TIMEOUT);
          },
          onPointerLeave: () => {
            isTriggerHovered.current = false;
            setTimeout(() => {
              if (!isContentHovered.current && !isTriggerHovered.current) {
                setIsOpen(false);
              }
            }, HOVER_LEAVE_TIMEOUT);
          },
        },
        contentHoverHandlers: {
          onPointerEnter: () => {
            isContentHovered.current = true;
          },
          onPointerLeave: () => {
            isContentHovered.current = false;
            setTimeout(() => {
              if (!isTriggerHovered.current) {
                setIsOpen(false);
              }
            }, HOVER_LEAVE_TIMEOUT);
          },
        },
      };
    }, [hasInternalHoverHandlers]);

    useEffect(() => {
      if (isLoaded) {
        onVisibilityChange(isOpen);
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeTooltip();
        }
      };

      if (isOpen && !hasOuterControl) {
        document.addEventListener('keydown', handleKeyDown);
      }

      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, hasOuterControl]);

    useEffect(() => {
      // isVisible !== undefined - эта проверка нужна для корректной работы ТС (сталкивались с проблемой в ЖД)
      if (hasOuterControl && isVisible !== undefined) {
        setIsOpen(isVisible);
      }
    }, [hasOuterControl, isVisible]);

    useEffect(() => {
      setIsLoaded(true);
    }, []);

    const triggerRef = useRef<HTMLElement>(null);

    const handlerTriggerClick = (event: React.MouseEvent) => {
      onTriggerClick(event);

      if (hasOuterControl || (isTouchDevice && isDisabledOnTouchDevice)) {
        return null;
      }

      if (triggerMode === 'click' || isTouchDevice) {
        setIsOpen(!isOpen);
      }
    };

    const renderTrigger = () => {
      if (customTargetRef) {
        return null;
      }

      return (
        <span
          className={cls(styles.trigger, triggerClassName)}
          ref={triggerRef}
          data-locator={cls(specificLocator, `${baseLocator}-trigger`, {
            hoverable: Boolean(triggerHoverHandlers),
            clickable: !triggerHoverHandlers,
          })}
          style={triggerStyle}
          {...triggerHoverHandlers}
          onClick={handlerTriggerClick}
        >
          {children}
        </span>
      );
    };

    const renderContent = () => {
      if (!content) {
        return null;
      }

      return (
        <Content
          baseLocator={baseLocator}
          childrenClassNames={childrenClassNames}
          className={className}
          closeTooltip={closeTooltip}
          content={content}
          contentHoverHandlers={contentHoverHandlers}
          contentMargin={CONTENT_OFFSET}
          hasCloser={hasCloser}
          hasOuterControl={hasOuterControl}
          isOpen={isOpen}
          pagePadding={PAGE_PADDING}
          placement={placement}
          preset={preset}
          specificLocator={specificLocator}
          targetRef={customTargetRef || triggerRef}
        />
      );
    };

    return (
      <>
        {renderTrigger()}
        {renderContent()}
      </>
    );
  };
};

const Tooltip = createTooltip();

export default memo(Tooltip);

export { Tooltip as TooltipWithoutMemo };
